import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { CheckCircle2, Circle, Clock, Rocket } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { ownerChecklist, ownerChecklistSeen } from "../../lib/api";

const Item = ({ it, onGo }) => (
  <li data-testid={`owner-check-${it.id}`} data-done={it.done ? "1" : "0"}>
    <Link to={it.to} onClick={onGo} className={`flex items-start gap-3 rounded-xl border px-3 py-2.5 transition-colors ${it.done ? "border-emerald-300/60 bg-emerald-50/40 dark:bg-emerald-900/10" : "border-border bg-card hover:border-primary"}`}>
      {it.done ? <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0" /> : it.pending ? <Clock className="h-5 w-5 text-amber-600 shrink-0" /> : <Circle className="h-5 w-5 text-muted-foreground shrink-0" />}
      <span className="min-w-0"><span className={`block text-sm font-medium ${it.done ? "line-through text-muted-foreground" : ""}`}>{it.label}</span>
        <span className="block text-xs text-muted-foreground">{it.pending && !it.done ? "Invite sent — waiting for them to join." : it.hint}</span></span>
    </Link>
  </li>
);

// Owner first-run: one-time pop-up after sign-up, then a Vault card until all four steps are done.
export const OwnerChecklist = () => {
  const qc = useQueryClient();
  const { data } = useQuery({ queryKey: ["owner-checklist"], queryFn: ownerChecklist, refetchOnWindowFocus: true });
  const [popup, setPopup] = useState(false);
  const complete = data && data.total > 0 && data.done >= data.total;
  useEffect(() => { if (data && data.total && !data.popup_seen && !complete) setPopup(true); }, [data, complete]);
  if (!data || !data.total || complete) return null;
  const close = () => { setPopup(false); ownerChecklistSeen().then(() => qc.invalidateQueries({ queryKey: ["owner-checklist"] })).catch(() => {}); };
  const list = (onGo) => <ul className="grid sm:grid-cols-2 gap-2">{data.items.map((it) => <Item key={it.id} it={it} onGo={onGo} />)}</ul>;
  return (
    <>
      <section className="mt-6 rounded-2xl border border-primary/30 bg-primary/5 p-5" data-testid="owner-checklist-card">
        <div className="flex items-center gap-2"><Rocket className="h-4 w-4 text-primary" /><h2 className="font-serif text-lg flex-1">Set up your family vault</h2><span className="text-xs font-mono text-primary" data-testid="owner-checklist-progress">{data.done} of {data.total} done</span></div>
        <p className="text-xs text-muted-foreground mt-1 mb-3">Four things that make HearthVault useful on the day your family needs it.</p>
        {list()}
      </section>
      <Dialog open={popup} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-lg" data-testid="owner-checklist-popup">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Welcome — your vault is ready</DialogTitle>
            <DialogDescription>Do these four in any order. This list stays at the top of your Vault until they're done.</DialogDescription></DialogHeader>
          {list(close)}
          <Button className="rounded-full mt-2" onClick={close} data-testid="owner-checklist-popup-close">Got it</Button>
        </DialogContent>
      </Dialog>
    </>
  );
};
